import { useQuery } from '@tanstack/react-query';
import { KeyRound, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import { CopyButton } from '@/components/ui/copy-button';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { authApi, type TokenResponse } from './api';
import { BackButton, MfaCodeForm, StepHeading } from './mfa-verify-step';

function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

export function MfaSetupStep({
  challengeToken,
  onVerified,
  onBack,
}: {
  challengeToken: string;
  onVerified: (token: TokenResponse) => void;
  onBack: () => void;
}) {
  const setup = useQuery({
    queryKey: ['mfa-setup', challengeToken],
    queryFn: async () => {
      const res = await authApi.mfaSetup(challengeToken);
      const qr = await QRCode.toDataURL(res.otpauthUrl, {
        margin: 1,
        width: 360,
        errorCorrectionLevel: 'M',
        color: { dark: '#2B323B', light: '#FFFFFF' },
      });
      return { ...res, qr };
    },
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  return (
    <div className="flex flex-col gap-6">
      <StepHeading
        icon={<QrCode className="size-5" aria-hidden />}
        title="Set up two-step verification"
        description="Administrator accounts are protected with an authenticator app. Scan the code, then enter the 6-digit code it shows."
      />

      {setup.isPending ? (
        <div className="flex items-center gap-4 rounded-xl border border-border bg-surface-2/50 p-4">
          <Skeleton className="size-36 rounded-lg" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      ) : setup.isError ? (
        <ErrorState error={setup.error} onRetry={() => void setup.refetch()} compact />
      ) : (
        <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-surface-2/50 p-4 sm:flex-row sm:items-start">
          <img
            src={setup.data.qr}
            alt="QR code to add this account to your authenticator app"
            className="size-36 shrink-0 rounded-lg bg-white p-1.5 shadow-e1 ring-1 ring-border"
          />
          <div className="flex min-w-0 flex-col gap-2 text-sm">
            <ol className="list-decimal space-y-1 pl-4 text-fg-muted marker:text-fg-subtle">
              <li>Open Google Authenticator, Microsoft Authenticator or 1Password.</li>
              <li>Scan the QR code, or enter the key below.</li>
            </ol>
            <div className="mt-1 flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1.5">
              <KeyRound className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
              <code
                className="tabular min-w-0 flex-1 truncate font-mono text-[12.5px] tracking-wide text-fg"
                aria-label="Setup key"
                data-testid="mfa-secret"
                data-secret={setup.data.secret}
                title={setup.data.secret}
              >
                {formatSecret(setup.data.secret)}
              </code>
              <CopyButton value={setup.data.secret} label="Copy setup key" />
            </div>
          </div>
        </div>
      )}

      <MfaCodeForm challengeToken={challengeToken} onVerified={onVerified} submitLabel="Verify and sign in" />
      <BackButton onClick={onBack} />
    </div>
  );
}
